import { useTranslation } from 'react-i18next';

import { PosizioneBoard } from '@/components/myBoards/PosizioneBoard';
import { useHaptics } from '@/hooks/useHaptics';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { useNomeGiocatore } from '@/hooks/useNomeGiocatore';
import { cn } from '@/lib/cn';
import { formatData, formatOra } from '@/lib/fmt';
import { contestoDi, latoVincente, momentoDi } from '@/lib/myBoards/riga';
import type { BoardSource, BoardView, PositionStatsView } from '@/types/myBoards';

/** Il colore della pastiglia di provenienza: Giocata col brand, Guardata caldo, Replay neutro (T4). */
const PASTIGLIA: Record<BoardSource, string> = {
  player: 'bg-brand/15 text-brand',
  spectator: 'bg-warning/15 text-warning',
  replay: 'bg-bg-tertiary text-fg',
};

/**
 * Una riga della lista «My Boards» (T4): miniatura, giocatori, momento (mossa, gioco, punteggio), contesto, provenienza e
 * pip. Tutta la riga e' un bottone (≥ 44 px): apre la scheda. Il pip e' un valore di `positionStats` (Q3), non si calcola qui.
 */
export function BoardRiga({
  board,
  stats,
  mioId,
  adesso,
  onApri,
}: {
  board: BoardView;
  stats: PositionStatsView;
  mioId: string | null;
  adesso: Date;
  onApri: (board: BoardView) => void;
}) {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  const lingua = useLinguaFormato();
  const nome = useNomeGiocatore(mioId);
  const { position, origin } = board;

  const quando = momentoDi(board.createdAt, adesso);
  const quandoTesto =
    quando.kind === 'today'
      ? t('myBoards.row.when.today', { time: formatOra(board.createdAt, lingua) })
      : quando.kind === 'yesterday'
        ? t('myBoards.row.when.yesterday')
        : formatData(board.createdAt, { day: 'numeric', month: 'short' }, lingua);

  const contesto = contestoDi(board);
  const vincitore = latoVincente(board);
  const contestoTesto =
    contesto === 'finished'
      ? vincitore
        ? t('myBoards.row.context.finishedWinner', { winner: nome(origin.players[vincitore]), when: quandoTesto })
        : t('myBoards.row.context.finished', { when: quandoTesto })
      : t(`myBoards.row.context.${contesto}`, { when: quandoTesto });

  const titolo = `${nome(origin.players.W)} – ${nome(origin.players.B)}`;

  return (
    <button
      type="button"
      data-testid={`board-riga-${board.id}`}
      onClick={() => {
        haptics.selection();
        onApri(board);
      }}
      className="card-edge flex min-h-11 w-full items-center gap-3 rounded-2xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
    >
      <div className="w-[34%] max-w-32 shrink-0">
        <PosizioneBoard
          position={position}
          size="mini"
          ariaLabel={t('myBoards.board.aria', { move: origin.ply })}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="break-words text-sm font-bold text-fg">{titolo}</span>
        <span className="text-xs text-fg-muted">
          {t('myBoards.row.move', {
            ply: origin.ply,
            game: position.match.gameNumber,
            scoreW: position.match.scoreW,
            scoreB: position.match.scoreB,
            length: position.match.length,
          })}
        </span>
        <span className="text-xs text-fg-muted">{contestoTesto}</span>
        <span className="mt-1 flex flex-wrap gap-1.5">
          <span
            data-testid={`board-origine-${board.id}`}
            className={cn('rounded-full px-2.5 py-1 text-[11px] font-bold', PASTIGLIA[board.capturedFrom])}
          >
            {t(`myBoards.row.source.${board.capturedFrom}`)}
          </span>
          <span className="rounded-full bg-bg-tertiary px-2.5 py-1 text-[11px] font-bold text-fg">
            {t('myBoards.row.pip', { white: stats.pip.W, black: stats.pip.B })}
          </span>
        </span>
      </div>
    </button>
  );
}
